/**
 * One finding, whatever stored it. The findings queue lists every compliance
 * gap: regulatory findings (`PolicyFinding`) and the two suggestion kinds that
 * are gaps — a control amendment and a missing document. Each is read here
 * into one shape, so the queue, the detail page and the policy tab never ask
 * which table a row came from.
 *
 * Status speaks the redesign's language: open, resolved, dismissed. A finding
 * "closed" with a reason and a suggestion "dismissed" are both dismissed; a
 * suggestion "accepted" (the retired in-progress stage) is still open.
 *
 * @module components/regulation/finding/findingItem
 */
import { DISMISS_CATEGORY_LABELS, type DismissCategory } from "@repo/shared/findingPlanning";
import {
  findingQueueStatus,
  suggestionBasis,
  suggestionControlCode,
  suggestionDocumentName,
  suggestionQueueStatus,
  suggestionRequiredChanges,
  toFindingSeverity,
  type FindingQueueBasis,
  type FindingQueueSeverity,
  type FindingQueueStatus,
} from "@repo/shared/findingQueue";

import type { PolicyFinding, Recommendation } from "../../../state/governance";
import { firstSentence } from "../tldr";
import { findingStalenessReason } from "./findingView";

// The classification rules — what is a finding, its status, its basis and
// the document it is filed under — are `@repo/shared/findingQueue`, so every
// surface that reports findings counts the same gaps as this queue.
export type Severity = FindingQueueSeverity;
export type FindingItemStatus = FindingQueueStatus;
export type FindingBasisKind = FindingQueueBasis;

export const SEVERITY_ORDER: ReadonlyArray<Severity> = ["critical", "high", "medium", "low"];
export const SEVERITY_LABEL: Readonly<Record<Severity, string>> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};
/** Critical 4 down to low 1; an unrated finding 0, below every rated one. */
export const severityRank = (severity: Severity | null) =>
  severity === null ? 0 : SEVERITY_ORDER.length - SEVERITY_ORDER.indexOf(severity);
export const BASIS_LABEL: Readonly<Record<FindingBasisKind, string>> = {
  regulation: "Regulation",
  control: "Control gap",
  missing: "Missing document",
};

export interface FindingBasis {
  readonly kind: FindingBasisKind;
  /** A chip's worth: "Guernsey", "SAN-1", "Missing". */
  readonly short: string;
  /** The full line: "Regulation · Guernsey · AML/CFT Handbook". */
  readonly label: string;
  /** The publication, for a regulation. */
  readonly url: string | null;
}

/** A state worth a chip on an open row. One per row, in priority order. */
export type FindingChip =
  | { readonly kind: "needs-recheck"; readonly label: string; readonly detail: string }
  | { readonly kind: "in-progress"; readonly label: string; readonly detail: string };

export interface FindingItem {
  readonly id: string;
  readonly origin: "finding" | "suggestion";
  readonly title: string;
  /** The gap in one sentence. */
  readonly problem: string;
  /** The gap in full: what this means for the document. */
  readonly gap: string;
  /** What the rule or source says, for the "Full wording" panel. */
  readonly published: string | null;
  readonly severity: Severity | null;
  readonly status: FindingItemStatus;
  readonly basis: FindingBasis;
  /** Null for a missing document: there is no document yet. */
  readonly policyId: string | null;
  readonly documentName: string;
  readonly requiredChanges: ReadonlyArray<string>;
  readonly raisedAt: number;
  readonly raisedBy: "Inspector" | "Monitor" | "eve" | "Added by hand";
  readonly assigneeId: string | null;
  /** `YYYY-MM-DD`. */
  readonly dueDate: string | null;
  readonly chip: FindingChip | null;
  readonly decision: FindingDecision | null;
  /** The record it was read from, for the parts of a page that need it. */
  readonly finding: PolicyFinding | null;
  readonly suggestion: Recommendation | null;
}

export interface FindingDecision {
  readonly status: "resolved" | "dismissed";
  readonly by: string | null;
  readonly at: string | null;
  /** "File v4", "Uploaded AML Policy.pdf", or null for a note-only decision. */
  readonly evidence: string | null;
  readonly category: DismissCategory | null;
  readonly note: string | null;
}

const toSeverity = toFindingSeverity;

/** "guernsey" → "Guernsey", "isle-of-man" → "Isle of Man". */
export function jurisdictionLabel(slug: string): string {
  const words = slug.split(/[-_\s]+/).filter(Boolean);
  return words
    .map((word, index) => (index > 0 && ["of", "and", "the"].includes(word) ? word : word[0]!.toUpperCase() + word.slice(1)))
    .join(" ");
}

const time = (value: string | null | undefined) => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const findingStatus = (status: PolicyFinding["status"]): FindingItemStatus => findingQueueStatus(status);

const suggestionStatus = (status: Recommendation["status"]): FindingItemStatus => suggestionQueueStatus(status);

/** A regulatory finding as a queue item. */
export function findingItemFromFinding(
  finding: PolicyFinding,
  documentNames: ReadonlyMap<string, string>,
): FindingItem {
  const status = findingStatus(finding.status);
  const jurisdiction = finding.jurisdictionSlug ? jurisdictionLabel(finding.jurisdictionSlug) : "";
  const source = finding.sourceTitle?.trim() || finding.title;
  const stale = findingStalenessReason(finding);
  const gap = finding.assessment?.trim() || finding.summary;
  return {
    id: finding.id,
    origin: "finding",
    title: finding.title,
    problem: firstSentence(gap),
    gap,
    published: finding.summary || null,
    severity: toSeverity(finding.severity),
    status,
    basis: {
      kind: "regulation",
      short: jurisdiction || "Regulation",
      label: [jurisdiction, source].filter(Boolean).join(" · "),
      url: finding.sourceUrl || null,
    },
    policyId: finding.policyId,
    documentName: documentNames.get(finding.policyId) ?? "Document",
    requiredChanges: finding.requiredChanges,
    raisedAt: time(finding.createdAt) || time(finding.detectedAt),
    raisedBy: finding.source === "monitor" ? "Monitor" : finding.source === "agent" ? "Inspector" : "Added by hand",
    assigneeId: finding.assigneeId ?? null,
    dueDate: finding.dueDate ?? null,
    chip: stale ? { kind: "needs-recheck", label: "Needs re-check", detail: stale } : null,
    decision:
      status === "open"
        ? null
        : {
            status,
            by: finding.resolvedByEmail,
            at: finding.resolvedAt,
            evidence: status === "resolved" && finding.resolvedVersion ? `File v${finding.resolvedVersion}` : null,
            category: finding.dismissCategory ?? null,
            note: finding.resolutionNote,
          },
    finding,
    suggestion: null,
  };
}

/** A gap suggestion as a queue item; null for a record update. */
export function findingItemFromSuggestion(
  suggestion: Recommendation,
  documentNames: ReadonlyMap<string, string>,
): FindingItem | null {
  const basis = suggestionBasis(suggestion.kind);
  if (basis === null) return null;
  const status = suggestionStatus(suggestion.status);
  const missing = basis === "missing";
  const proposedChange = suggestion.proposedChange as Record<string, unknown>;
  const control = suggestionControlCode(proposedChange);
  const documentName = suggestionDocumentName(suggestion, documentNames);
  const evidence =
    status !== "resolved"
      ? null
      : missing
        ? suggestion.resolvedPolicyName
          ? `Uploaded ${suggestion.resolvedPolicyName}`
          : null
        : suggestion.resolvedVersion
          ? `File v${suggestion.resolvedVersion}`
          : null;
  return {
    id: suggestion.id,
    origin: "suggestion",
    title: suggestion.title,
    problem: firstSentence(suggestion.rationale),
    gap: suggestion.rationale,
    published: null,
    severity: toSeverity(suggestion.severity),
    status,
    basis: missing
      ? { kind: "missing", short: "Missing", label: "Missing document", url: null }
      : {
          kind: "control",
          short: control ?? "Control",
          label: ["Control gap", control].filter(Boolean).join(" · "),
          url: null,
        },
    policyId: suggestion.policyId,
    documentName,
    requiredChanges: suggestionRequiredChanges(proposedChange),
    raisedAt: time(suggestion.createdAt),
    raisedBy: suggestion.source === "inspector" ? "Inspector" : suggestion.source === "agent" ? "eve" : "Added by hand",
    assigneeId: suggestion.assigneeId ?? null,
    dueDate: suggestion.dueDate ?? null,
    chip:
      suggestion.status === "accepted"
        ? { kind: "in-progress", label: "Marked in progress", detail: "Someone marked this in progress before assignees existed." }
        : null,
    decision:
      status === "open"
        ? null
        : {
            status,
            by: suggestion.decidedByEmail,
            at: suggestion.decidedAt,
            evidence,
            category: suggestion.dismissCategory ?? null,
            note: suggestion.decisionNote,
          },
    finding: null,
    suggestion,
  };
}

/** Every finding: regulatory findings and gap suggestions together. */
export function findingItems(
  findings: ReadonlyArray<PolicyFinding>,
  suggestions: ReadonlyArray<Recommendation>,
  documentNames: ReadonlyMap<string, string>,
): FindingItem[] {
  return [
    ...findings.map((finding) => findingItemFromFinding(finding, documentNames)),
    ...suggestions.flatMap((suggestion) => {
      const item = findingItemFromSuggestion(suggestion, documentNames);
      return item === null ? [] : [item];
    }),
  ];
}

/** "Resolved by File v4", "Dismissed · Already covered". */
export function decisionSummary(decision: FindingDecision): string {
  if (decision.status === "resolved") return decision.evidence ? `Resolved by ${decision.evidence}` : "Resolved";
  return decision.category ? `Dismissed · ${DISMISS_CATEGORY_LABELS[decision.category]}` : "Dismissed";
}

/** What to do next, in one short sentence (the TL;DR's last line). */
export function nextStep(item: FindingItem, options: { readonly canDecide: boolean; readonly hasFix?: boolean }): string {
  if (item.status === "resolved") return "Resolved. Reopen it if the gap comes back.";
  if (item.status === "dismissed") return "Dismissed. Reopen it if this was wrong.";
  if (!options.canDecide) return "Waiting for an admin to resolve or dismiss it. You can comment.";
  if (item.chip?.kind === "needs-recheck") return "The document changed after the assessment. Re-check it, then decide.";
  if (item.basis.kind === "missing") return "Upload the document to close this gap, or dismiss it.";
  if (options.hasFix) return "Review the proposed fix, then apply it.";
  return "Upload a version that closes the gap, then resolve with it. Or dismiss the finding.";
}
